import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { setCanonical, setMeta } from '@/utils/seo';
import { OrganizationSchema, WebSiteSchema, ServiceSchema, FAQSchema, SpeakableSchema, HowToSchema } from '@/components/SEOSchemas';
import GetStartedModal from '@/components/GetStartedModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HOME_FAQS = [
  { question: "What is LegaSecure?", answer: "LegaSecure is a digital estate planning platform for Filipinos worldwide. It provides Online SPA (Special Power of Attorney), Guardian Mandates, and a Zero-Knowledge Digital Vault to help you manage your Philippine affairs from anywhere." },
  { question: "Who is LegaSecure for?", answer: "LegaSecure serves OFWs (Overseas Filipino Workers), Filipino families, and business owners who need to manage property, legal documents, and family protection plans remotely." },
  { question: "How much does LegaSecure cost?", answer: "LegaSecure offers three plans: Free Basic Access (P0/forever) with 5 documents, Standard (P249/month) with Online SPA and 100GB storage, and Family/Premium (P499/month) with unlimited storage and up to 3 Legacy Contacts." },
  { question: "What is an Online SPA?", answer: "An Online SPA (Special Power of Attorney) lets you authorize a family member to handle bank transactions, sell property, pay bills, or sign contracts on your behalf without you needing to fly home to the Philippines." },
  { question: "What is a Guardian Mandate?", answer: "A Guardian Mandate designates who will care for your children if you become incapacitated. It includes care instructions, medical contacts, school records, and financial access details stored securely in LegaSecure's vault." },
  { question: "Is my data safe with LegaSecure?", answer: "Yes. LegaSecure uses Zero-Knowledge encryption, meaning your documents are encrypted on your device before being uploaded. Not even LegaSecure can see your data - only you and your designated contacts can access it." },
];

const HOW_IT_WORKS = [
  { name: "Create Your Account", text: "Sign up for free at legasecure.com. No credit card required to start protecting your legacy." },
  { name: "Upload Your Documents", text: "Store your important documents - land titles, bank details, insurance policies - in the encrypted Digital Vault." },
  { name: "Set Up Your Plan", text: "Create an Online SPA, designate guardians, and set conditional release rules for your documents." },
  { name: "Share with Trusted Contacts", text: "Designate family members who can access your vault when needed. You control who sees what." },
];

const TIPS_OF_WEEK = [
  { tip: "Always keep a certified copy of your land title in a secure digital vault. The original should be in a safe place, but having a digital backup ensures you can act quickly.", link: "/guides/how-to-transfer-land-title-philippines" },
  { tip: "If you're an OFW, create a Special Power of Attorney BEFORE you need one. Waiting until an emergency makes the process much harder and more expensive.", link: "/guides/special-power-of-attorney-philippines" },
  { tip: "Update your guardian mandate every year. Children's needs change — their schools, doctors, medications, and routines evolve over time.", link: "/guides/guardian-mandate-filipino-children" },
  { tip: "Never store passwords in plain text — even in a safe. Use an encrypted digital vault with zero-knowledge security so only you can access them.", link: "/guides/digital-legacy-planning" },
  { tip: "The Philippine estate tax is 6% on net estate above P5 million. Proper planning can help your family minimize this burden.", link: "/guides/philippine-inheritance-law" },
  { tip: "An SPA (Special Power of Attorney) can be used for property sales, bank transactions, and legal matters. Make sure yours specifies exactly what your agent can do.", link: "/guides/special-power-of-attorney-philippines" },
  { tip: "Philippine consulates abroad can notarize SPAs, but processing times vary. Plan 2-4 weeks for traditional SPA processing — or use LegaSecure's online option.", link: "/guides/apostille-requirements-ofw" },
  { tip: "Extra-judicial settlement is the fastest way to divide an estate when all heirs agree. It avoids costly court proceedings.", link: "/guides/how-to-sell-property-philippines-while-abroad" },
  { tip: "If you have crypto or digital assets, include them in your estate plan. Without proper instructions, your family may never be able to access them.", link: "/guides/digital-legacy-planning" },
  { tip: "Document everything about your children's daily routine — schools, activities, allergies, bedtimes. Your guardian needs this information to provide continuity.", link: "/guides/guardian-mandate-filipino-children" },
  { tip: "Capital gains tax on property sales in the Philippines is 6% of selling price or zonal value (whichever is higher). Budget for this when planning a sale.", link: "/guides/how-to-sell-property-philippines-while-abroad" },
  { tip: "Review your estate plan after major life events: marriage, birth of a child, property purchase, job change, or relocation to a new country.", link: "/guides/estate-planning-for-ofw" },
  { tip: "Apostille certification is required for Philippine documents used in Hague Convention countries. The DFA processes apostille for P100 per document.", link: "/guides/apostille-requirements-ofw" },
  { tip: "A will is not required in the Philippines — intestate succession applies automatically. But having one ensures your wishes are followed precisely.", link: "/guides/philippine-inheritance-law" },
  { tip: "OFWs can now access many Philippine government services online, including SSS, Pag-IBIG, and PhilHealth. But property transactions still require SPA.", link: "/guides/estate-planning-for-ofw" },
  { tip: "If you own property in multiple Philippine cities, create separate SPA documents for each property to avoid complications.", link: "/guides/special-power-of-attorney-philippines" },
  { tip: "Always use a notarized SPA — a simple written authorization is not legally sufficient for major transactions like property sales.", link: "/guides/special-power-of-attorney-philippines" },
  { tip: "Your guardian mandate should include financial access information — how to access your bank accounts for your children's immediate needs.", link: "/guides/guardian-mandate-filipino-children" },
  { tip: "Consider setting up conditional access rules — documents that are automatically released to your family if you don't log in for a specified period.", link: "/guides/digital-legacy-planning" },
  { tip: "The Philippines has compulsory heirs — your spouse and children cannot be completely disinherited. Know your legal obligations before making a will.", link: "/guides/philippine-inheritance-law" },
  { tip: "Keep your emergency contacts updated across all countries. If something happens to you abroad, your family needs to know who to call locally.", link: "/guides/estate-planning-for-ofw" },
  { tip: "Documentary stamp tax (1.5% of selling price) is separate from capital gains tax. Budget for both when selling property.", link: "/guides/how-to-transfer-land-title-philippines" },
  { tip: "A digital vault is safer than a safety deposit box — it's accessible from anywhere, can't be destroyed by fire or flood, and uses military-grade encryption.", link: "/guides/digital-legacy-planning" },
  { tip: "If you're an OFW returning home permanently, update your estate plan to reflect your new situation. Documents created abroad may need to be re-registered.", link: "/guides/estate-planning-for-ofw" },
  { tip: "Name alternate guardians in case your primary guardian is unavailable. Always have a backup plan for your backup plan.", link: "/guides/guardian-mandate-filipino-children" },
  { tip: "Transfer tax rates vary by city — typically 0.5% to 0.75% of the property value. Check with the local Treasurer's Office.", link: "/guides/how-to-transfer-land-title-philippines" },
  { tip: "An SPA expires when the specific task is completed, when revoked, or when either party dies. Make sure yours covers the right timeframe.", link: "/guides/special-power-of-attorney-philippines" },
  { tip: "If both parents are OFWs, guardian mandates are doubly important. Both should designate the same guardian to avoid legal conflicts.", link: "/guides/guardian-mandate-filipino-children" },
  { tip: "Some banks require a specifically worded SPA for account access. Check with your bank about their exact requirements before creating your SPA.", link: "/guides/special-power-of-attorney-philippines" },
  { tip: "Your estate plan should include insurance policies, retirement accounts, and investment information — not just property and bank accounts.", link: "/guides/estate-planning-for-ofw" },
  { tip: "The Habilin (final wishes) is a Filipino tradition of leaving instructions for loved ones. LegaSecure digitizes this tradition for the modern world.", link: "/guides/estate-planning-for-ofw" },
  { tip: "Registry of Deeds in the Philippines can take 1-3 months to process title transfers. Plan ahead and have all documents ready.", link: "/guides/how-to-transfer-land-title-philippines" },
  { tip: "OWWA (Overseas Workers Welfare Administration) provides death and disability benefits for OFWs. Make sure your family knows how to claim these.", link: "/guides/estate-planning-for-ofw" },
  { tip: "A BIR Certificate Authorizing Registration (CAR) is required before any property title transfer. Don't skip this step.", link: "/guides/how-to-transfer-land-title-philippines" },
  { tip: "Digital estate planning isn't just for the wealthy. Every Filipino with a bank account, property, or children needs a plan.", link: "/guides/estate-planning-for-ofw" },
  { tip: "Store your children's passport copies, birth certificates, and school records in your digital vault. Your guardian may need these urgently.", link: "/guides/guardian-mandate-filipino-children" },
  { tip: "Review your SPA annually. Laws change, agents move, and your needs evolve. An outdated SPA can cause more problems than no SPA.", link: "/guides/special-power-of-attorney-philippines" },
  { tip: "Consider recording a video message for your family alongside your written estate plan. It adds a personal touch that documents can't.", link: "/guides/digital-legacy-planning" },
  { tip: "The Philippine Real Property Tax is due every January. If you're abroad, make sure someone is paying this — delinquency can lead to property auction.", link: "/guides/how-to-sell-property-philippines-while-abroad" },
  { tip: "Your legacy isn't just about money and property — it's about the values, instructions, and love you leave behind for your family.", link: "/guides/estate-planning-for-ofw" },
  { tip: "Protect your social media accounts in your estate plan. Facebook and Instagram have legacy contact features — set them up now.", link: "/guides/digital-legacy-planning" },
  { tip: "If you're buying property in the Philippines from abroad, you STILL need an SPA for the signing. Plan this before closing the deal.", link: "/guides/special-power-of-attorney-philippines" },
  { tip: "LegaSecure's Zero-Knowledge encryption means even LegaSecure cannot see your documents. You hold the only key. That's true security.", link: "/guides/digital-legacy-planning" },
  { tip: "An OFW's biggest asset is often their remittance stream. Plan for what happens to your family's finances if that stream suddenly stops.", link: "/guides/estate-planning-for-ofw" },
  { tip: "Compulsory heirs in the Philippines include: legitimate children, surviving spouse, and legitimate parents. They cannot be disinherited.", link: "/guides/philippine-inheritance-law" },
  { tip: "The Philippine Consulate in your country can provide a list of required documents for SPA notarization. Call ahead to save time.", link: "/guides/apostille-requirements-ofw" },
  { tip: "Don't wait for a crisis to plan. The best time to set up your estate plan was yesterday. The second best time is today.", link: "/guides/estate-planning-for-ofw" },
  { tip: "Your digital vault should be as organized as a filing cabinet — label documents clearly and group them by category for easy access.", link: "/guides/digital-legacy-planning" },
  { tip: "Intestate succession in the Philippines follows a strict order: legitimate children first, then surviving spouse, then parents, then siblings.", link: "/guides/philippine-inheritance-law" },
  { tip: "A guardian mandate is not just for death scenarios — it covers incapacitation too. Even a temporary disability means your children need care.", link: "/guides/guardian-mandate-filipino-children" },
  { tip: "SSS, Pag-IBIG, and PhilHealth death benefits can be significant. Make sure your family knows about ALL your government memberships.", link: "/guides/estate-planning-for-ofw" },
  { tip: "End of year is a great time for estate planning review. Update your documents, check your vault, and make sure everything is current for the new year.", link: "/guides/estate-planning-for-ofw" },
];

export default function HomePage() {
  const [cities, setCities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [sessionCount, setSessionCount] = useState(1000);

  useEffect(() => {
    setCanonical('/');
    setMeta('LegaSecure Guide | Estate Planning & Legacy Protection for Filipinos', 'Your complete guide to estate planning, SPA, and legacy protection for Filipinos worldwide. Serving 150+ Philippine cities and 50+ OFW countries.');
    axios.get(`${API}/cities?limit=12`).then(r => setCities(r.data.cities?.slice(0, 12) || [])).catch(() => {});
    axios.get(`${API}/countries?limit=12`).then(r => setCountries(r.data.countries?.slice(0, 12) || [])).catch(() => {});
    axios.get(`${API}/session-count`).then(r => setSessionCount(r.data.count)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <OrganizationSchema />
      <WebSiteSchema />
      <ServiceSchema />
      <FAQSchema faqs={HOME_FAQS} />
      <SpeakableSchema selectors={["h1", ".speakable-intro"]} />
      <HowToSchema name="How to Protect Your Legacy with LegaSecure" steps={HOW_IT_WORKS} />

      {/* Hero */}
      <section data-testid="hero-section" className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3NDE5ODJ8MHwxfHNlYXJjaHwxfHxlc3RhdGUlMjBwbGFubmluZ3xlbnwwfHx8fDE3NjIxMDA5MjV8MA&ixlib=rb-4.1.0&q=80&w=1920" alt="Estate Planning" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-white/75"></div>
        </div>
        <div className="ls-container relative z-10 py-16 md:py-24">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-block mb-4 px-4 py-1 rounded-full bg-[var(--ls-gold)]/15 text-[var(--ls-gold-dark)] text-sm font-medium">
              <i className="fa-solid fa-shield-halved mr-2"></i>
              Trusted by Filipinos worldwide <i className="fa-solid fa-earth-asia ml-1"></i>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold leading-tight mb-6 font-['Crimson_Pro']" style={{ color: '#1B365D' }}>
              Protect Your Life's Work and Your Family's Future — From Anywhere in the World.
            </h1>
            <p className="speakable-intro text-lg mb-8 leading-relaxed max-w-2xl mx-auto" style={{ color: '#4A5568' }}>
              You've worked hard to build a future for your loved ones — that legacy should be a gift, not a source of stress. LegaSecure makes it simple for Filipinos everywhere to share their Habilin — your final wishes and care instructions — in one secure place. Take the first step today and give your family the peace of mind they deserve.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a href="https://legasecure.com" data-testid="hero-cta-main" className="ls-btn-navy text-base px-8 py-3">
                Get Started for Free <i className="fa-solid fa-arrow-right ml-2"></i>
              </a>
              <Link to="/all-cities" data-testid="hero-cta-cities" className="ls-btn-outline text-base px-8 py-3">
                Browse Cities
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Cards */}
      <section className="bg-[var(--ls-bg)] py-8">
        <div className="ls-container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: 'fa-file-signature', label: 'Online SPA', sub: 'Apostille-Ready', link: '/guides/special-power-of-attorney-philippines' },
              { icon: 'fa-user-shield', label: 'Guardian Mandate', sub: 'Protect Your Kids', link: '/guides/guardian-mandate-filipino-children' },
              { icon: 'fa-vault', label: 'Digital Vault', sub: 'Zero-Knowledge', link: '/guides/digital-legacy-planning' },
              { icon: 'fa-globe', label: '50+ Countries', sub: 'OFW Coverage', link: '/all-countries' },
            ].map((item, i) => (
              <Link key={i} to={item.link} className="ls-card text-center hover:border-[var(--ls-gold)] border border-transparent">
                <i className={`fa-solid ${item.icon} text-[var(--ls-gold)] text-2xl mb-2`}></i>
                <p className="text-[var(--ls-navy)] font-semibold text-sm">{item.label}</p>
                <p className="text-[var(--ls-text-light)] text-xs">{item.sub}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="ls-section">
        <div className="ls-container">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">What LegaSecure Offers</h2>
          <p className="text-center text-[var(--ls-text-light)] mb-10 max-w-2xl mx-auto">Comprehensive digital estate planning tools designed specifically for Filipinos and OFWs.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: 'fa-file-signature', title: 'Online SPA', desc: 'Create Apostille-ready Special Power of Attorney from anywhere. Authorize family to handle bank transactions, sell property, or sign contracts without flying home.', color: 'bg-blue-50 text-blue-600', link: '/guides/special-power-of-attorney-philippines' },
              { icon: 'fa-user-shield', title: 'Guardian Mandate', desc: 'Designate who cares for your children if something happens. Store care instructions, medical info, school records, and emergency contacts securely.', color: 'bg-amber-50 text-amber-600', link: '/guides/guardian-mandate-filipino-children' },
              { icon: 'fa-vault', title: 'Digital Vault', desc: 'Zero-Knowledge encrypted storage for land titles, bank details, crypto keys, insurance policies, and more. Only you hold the key.', color: 'bg-emerald-50 text-emerald-600', link: '/guides/digital-legacy-planning' },
            ].map((s, i) => (
              <Link key={i} to={s.link} className="ls-card group" data-testid={`service-card-${i}`}>
                <div className={`w-12 h-12 rounded-xl ${s.color} flex items-center justify-center mb-4`}>
                  <i className={`fa-solid ${s.icon} text-xl`}></i>
                </div>
                <h3 className="text-xl font-bold mb-2 group-hover:text-[var(--ls-gold)] transition-colors">{s.title}</h3>
                <p className="text-[var(--ls-text-light)] text-sm leading-relaxed">{s.desc}</p>
                <p className="text-[var(--ls-gold)] text-sm font-medium mt-4">Learn more <i className="fa-solid fa-arrow-right ml-1"></i></p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Tip of the Week */}
      <section className="bg-white py-6">
        <div className="ls-container">
          <TipOfTheWeek />
        </div>
      </section>

      {/* Cities Grid */}
      <section className="ls-section bg-white" data-testid="cities-section">
        <div className="ls-container">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">Estate Planning Help in Your City</h2>
          <p className="text-center text-[var(--ls-text-light)] mb-10">Browse estate planning guides for Philippine cities</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {cities.map(city => (
              <Link key={city.slug} to={`/city/${city.slug}`} data-testid={`city-card-${city.slug}`}
                className="ls-card flex items-center gap-3 p-4 hover:border-[var(--ls-gold)] border border-transparent">
                <div className="w-10 h-10 rounded-lg bg-[var(--ls-bg)] flex items-center justify-center flex-shrink-0">
                  <i className="fa-solid fa-city text-[var(--ls-navy)] text-sm"></i>
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-sm text-[var(--ls-navy)] truncate">{city.name}</p>
                  <p className="text-xs text-[var(--ls-text-light)]">{city.region}</p>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/all-cities" data-testid="view-all-cities" className="ls-btn-outline text-sm">View All Philippine Cities <i className="fa-solid fa-arrow-right ml-2"></i></Link>
          </div>
        </div>
      </section>

      {/* Countries Grid */}
      <section className="ls-section" data-testid="countries-section">
        <div className="ls-container">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">OFW? Find Help for Your Country</h2>
          <p className="text-center text-[var(--ls-text-light)] mb-10">Estate planning guides for Filipinos working abroad</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {countries.map(c => (
              <Link key={c.slug} to={`/ofw/${c.slug}`} data-testid={`country-card-${c.slug}`}
                className="ls-card flex items-center gap-3 p-4 hover:border-[var(--ls-gold)] border border-transparent">
                <div className="w-10 h-10 rounded-lg bg-[var(--ls-bg)] flex items-center justify-center flex-shrink-0">
                  <i className="fa-solid fa-globe text-[var(--ls-navy)] text-sm"></i>
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-sm text-[var(--ls-navy)] truncate">{c.name}</p>
                  <p className="text-xs text-[var(--ls-text-light)]">{c.ofw_estimate} OFWs</p>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/all-countries" data-testid="view-all-countries" className="ls-btn-outline text-sm">View All OFW Countries <i className="fa-solid fa-arrow-right ml-2"></i></Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="ls-section bg-white">
        <div className="ls-container">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">How LegaSecure Works</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {HOW_IT_WORKS.map((step, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 rounded-full bg-[var(--ls-gold)] text-white text-xl font-bold flex items-center justify-center mx-auto mb-4">{i + 1}</div>
                <h3 className="font-bold mb-2">{step.name}</h3>
                <p className="text-sm text-[var(--ls-text-light)]">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Unbreakable Trust */}
      <section className="ls-section" data-testid="trust-section">
        <div className="ls-container">
          <div className="text-center mb-10">
            <p className="text-sm font-medium text-[var(--ls-gold)] uppercase tracking-wider mb-2">Unbreakable Trust</p>
            <h2 className="text-2xl md:text-3xl font-bold mb-3">Can LegaSecure See My Data? <span className="text-[var(--ls-gold)]">NO.</span></h2>
            <p className="text-[var(--ls-text-light)] max-w-2xl mx-auto">We use cryptographic security so strong, only you and your Guardian hold the key. We hold the vault, but you alone hold the key.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="ls-card text-center">
              <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mx-auto mb-4"><i className="fa-solid fa-key text-xl"></i></div>
              <h3 className="font-bold mb-2">Zero-Knowledge Proof</h3>
              <p className="text-sm text-[var(--ls-text-light)]">Your documents are encrypted on YOUR device before upload. We only store scrambled data — it's mathematically impossible for us to read it.</p>
            </div>
            <div className="ls-card text-center">
              <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center mx-auto mb-4"><i className="fa-solid fa-lock text-xl"></i></div>
              <h3 className="font-bold mb-2">Digital Asset Protection</h3>
              <p className="text-sm text-[var(--ls-text-light)]">Safely store crypto keys, bank passwords, and critical credentials. No one — not even LegaSecure employees — can access your private data.</p>
            </div>
            <div className="ls-card text-center">
              <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-4"><i className="fa-solid fa-clipboard-check text-xl"></i></div>
              <h3 className="font-bold mb-2">Complete Audit Trail</h3>
              <p className="text-sm text-[var(--ls-text-light)]">Every access attempt, modification, and signature is recorded and permanently timestamped. Full transparency for your peace of mind.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof - Why Trust */}
      <section className="bg-white py-10">
        <div className="ls-container">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16 text-center">
            <div>
              <p className="text-3xl font-bold text-[var(--ls-navy)]">10M+</p>
              <p className="text-xs text-[var(--ls-text-light)]">OFWs Worldwide</p>
            </div>
            <div className="w-px h-10 bg-[var(--ls-border)] hidden md:block"></div>
            <div>
              <p className="text-3xl font-bold text-[var(--ls-navy)]">80+</p>
              <p className="text-xs text-[var(--ls-text-light)]">Countries Covered</p>
            </div>
            <div className="w-px h-10 bg-[var(--ls-border)] hidden md:block"></div>
            <div>
              <p className="text-3xl font-bold text-[var(--ls-navy)]">150+</p>
              <p className="text-xs text-[var(--ls-text-light)]">Philippine Cities</p>
            </div>
            <div className="w-px h-10 bg-[var(--ls-border)] hidden md:block"></div>
            <div>
              <p className="text-3xl font-bold text-[var(--ls-navy)]">Free</p>
              <p className="text-xs text-[var(--ls-text-light)]">To Get Started</p>
            </div>
          </div>
        </div>
      </section>

      {/* Legal Readiness Quiz */}
      <section className="ls-section bg-white" data-testid="quiz-section">
        <div className="ls-container max-w-3xl">
          <LegalReadinessQuiz />
        </div>
      </section>

      {/* FAQ */}
      <section className="ls-section" data-testid="faq-section">
        <div className="ls-container max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {HOME_FAQS.map((faq, i) => (
              <FAQItem key={i} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="ls-section bg-[var(--ls-navy)]">
        <div className="ls-container text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 font-['Crimson_Pro']">Protect Your Legacy. Start Your LegaSecure Plan Today.</h2>
          <p className="text-white/70 mb-8 max-w-xl mx-auto">You work hard for your family. Don't let distance and red tape undo your sacrifice.</p>
          <a href="https://legasecure.com" data-testid="bottom-cta" className="ls-btn-gold text-lg px-10 py-4">
            Start My Plan <i className="fa-solid fa-arrow-right ml-2"></i>
          </a>
        </div>
      </section>
    </div>
  );
}

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="ls-card cursor-pointer" onClick={() => setOpen(!open)}>
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-[var(--ls-navy)] pr-4">{question}</h3>
        <i className={`fa-solid fa-chevron-down text-[var(--ls-text-light)] transition-transform ${open ? 'rotate-180' : ''}`}></i>
      </div>
      {open && <p className="mt-3 text-[var(--ls-text-light)] text-sm leading-relaxed">{answer}</p>}
    </div>
  );
}


function TipOfTheWeek() {
  const weekNum = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 1)) / (7 * 24 * 60 * 60 * 1000));
  const tipIndex = weekNum % TIPS_OF_WEEK.length;
  const currentTip = TIPS_OF_WEEK[tipIndex];

  return (
    <div data-testid="tip-of-week" className="ls-card border-l-4 border-[var(--ls-gold)] flex items-start gap-4">
      <div className="w-10 h-10 rounded-full bg-[var(--ls-gold)]/10 flex items-center justify-center flex-shrink-0 mt-1">
        <i className="fa-solid fa-lightbulb text-[var(--ls-gold)]"></i>
      </div>
      <div>
        <p className="text-xs font-bold text-[var(--ls-gold)] uppercase tracking-wider mb-1">Tip of the Week</p>
        <p className="text-sm text-[var(--ls-text)] leading-relaxed">{currentTip.tip}</p>
        <Link to={currentTip.link} className="text-xs text-[var(--ls-gold)] font-medium mt-2 inline-block hover:underline">Read more <i className="fa-solid fa-arrow-right ml-1"></i></Link>
      </div>
    </div>
  );
}

function LegalReadinessQuiz() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [score, setScore] = useState(null);

  const questions = [
    { q: "Do you have a written will or estate plan?", yes: 2, no: 0 },
    { q: "Have you designated a guardian for your children?", yes: 2, no: 0 },
    { q: "Do you have a Special Power of Attorney (SPA) for managing assets remotely?", yes: 2, no: 0 },
    { q: "Are your important documents (land titles, insurance, bank details) stored securely and accessible to your family?", yes: 2, no: 0 },
    { q: "Have you informed your family about your wishes and where to find your documents?", yes: 2, no: 0 },
  ];

  const answer = (val) => {
    const newAnswers = [...answers, val];
    setAnswers(newAnswers);
    if (step < questions.length - 1) {
      setStep(step + 1);
    } else {
      const total = newAnswers.reduce((a, b) => a + b, 0);
      setScore(total);
    }
  };

  const reset = () => { setStep(0); setAnswers([]); setScore(null); };

  const getResult = () => {
    if (score >= 8) return { label: "Well Protected", color: "text-green-600", bg: "bg-green-50", icon: "fa-shield-halved", desc: "Great job! Your estate plan is in good shape. Consider upgrading to LegaSecure Premium for maximum protection." };
    if (score >= 4) return { label: "Partially Protected", color: "text-amber-600", bg: "bg-amber-50", icon: "fa-triangle-exclamation", desc: "You've started, but there are gaps. LegaSecure's Standard plan can help you fill them quickly." };
    return { label: "At Risk", color: "text-red-600", bg: "bg-red-50", icon: "fa-circle-exclamation", desc: "Your family is vulnerable. Start with LegaSecure's Free plan today — it takes less than 5 minutes." };
  };

  return (
    <div data-testid="legal-readiness-quiz">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">Are Your Philippine Assets Protected?</h2>
      <p className="text-center text-[var(--ls-text-light)] mb-8">Take this quick 5-question quiz to find out your Legal Readiness Score.</p>

      {score === null ? (
        <div className="ls-card text-center">
          <div className="flex justify-between items-center mb-6">
            <span className="text-xs text-[var(--ls-text-light)]">Question {step + 1} of {questions.length}</span>
            <div className="flex gap-1">
              {questions.map((_, i) => (
                <div key={i} className={`w-8 h-1.5 rounded-full ${i <= step ? 'bg-[var(--ls-gold)]' : 'bg-[var(--ls-border)]'}`}></div>
              ))}
            </div>
          </div>
          <h3 className="text-lg font-bold text-[var(--ls-navy)] mb-6">{questions[step].q}</h3>
          <div className="flex gap-4 justify-center">
            <button onClick={() => answer(questions[step].yes)} className="ls-btn-gold px-8" data-testid="quiz-yes">Yes</button>
            <button onClick={() => answer(questions[step].no)} className="ls-btn-outline px-8" data-testid="quiz-no">No</button>
          </div>
        </div>
      ) : (
        <div className={`ls-card text-center ${getResult().bg}`}>
          <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center mx-auto mb-4 shadow-md">
            <i className={`fa-solid ${getResult().icon} text-2xl ${getResult().color}`}></i>
          </div>
          <p className={`text-3xl font-bold ${getResult().color} mb-1`}>{score}/10</p>
          <p className={`text-lg font-bold ${getResult().color} mb-3`}>{getResult().label}</p>
          <p className="text-sm text-[var(--ls-text)] mb-6 max-w-md mx-auto">{getResult().desc}</p>
          <div className="flex gap-3 justify-center flex-wrap">
            <a href="https://legasecure.com" className="ls-btn-gold text-sm">Start Protecting Now</a>
            <button onClick={reset} className="ls-btn-outline text-sm">Take Again</button>
          </div>
          <p className="text-[10px] text-[var(--ls-text-light)] mt-4">This quiz is for informational purposes only and does not constitute legal advice.</p>
        </div>
      )}
    </div>
  );
}
